import { Contract } from '../types/contract';
import jsPDF from 'jspdf';

const formatDateLong = (dateString: string) => {
  if (!dateString) return '';
  
  try {
    let date: Date;
    
    // Se a data está em formato brasileiro (DD/MM/YYYY)
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else if (dateString.includes('-')) {
      // Se a data está em formato ISO (YYYY-MM-DD)
      date = new Date(dateString + 'T00:00:00');
    } else {
      // Outros formatos
      date = new Date(dateString);
    }
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    
    const dia = date.getDate();
    const mes = date.toLocaleString('pt-BR', { month: 'long' });
    const ano = date.getFullYear();
    return `${dia} de ${mes} de ${ano}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error, 'Data original:', dateString);
    return 'Data inválida';
  }
};

const formatValueLong = (value: string | undefined) => {
  if (!value) return '';
  const numValue = parseFloat(value.replace(',', '.'));
  if (!isNaN(numValue)) {
    const formattedNumber = numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formattedNumber} REAIS`;
  }
  return value;
};

const getMusicalRestrictionText = (value?: string) => {
  const cleaned = (value || '').trim();
  if (!cleaned || /^\d+([,.]\d{1,2})?$/.test(cleaned)) {
    return 'NAO POSSUEM';
  }

  return cleaned.toUpperCase();
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

export const pdfService = {
  async generateContractPDF(contract: Contract): Promise<Blob> {
    return new Promise<Blob>(async (resolve, reject) => {
      try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        const headerImg = await loadImage('/images/cabecalho.jpg');
        const footerImg = await loadImage('/images/rodape.jpg');
        
        const addText = (text: string, x: number, y: number, options: any = {}) => {
          const { fontSize = 10, fontStyle = 'normal', align = 'left', maxWidth = 180 } = options;
          pdf.setFontSize(fontSize);
          pdf.setFont('courier', fontStyle);
          const lines = pdf.splitTextToSize(text, maxWidth);
          let currentY = y;
          lines.forEach((line: string) => {
            if (currentY > 260) { // Reduzir para evitar sobreposição com rodapé
              pdf.addPage();
              addHeader();
              currentY = 50; // Aumentar para começar abaixo do cabeçalho
            }
            pdf.text(line, x, currentY);
            currentY += fontSize * 0.4;
          });
          return currentY;
        };
        
        const addHighlightedText = (text: string, x: number, y: number, options: any = {}) => {
          const { fontSize = 10, fontStyle = 'bold', align = 'left', maxWidth = 180 } = options;
          pdf.setFontSize(fontSize);
          pdf.setFont('courier', fontStyle);
          
          // Adicionar fundo amarelo
          const textWidth = pdf.getTextWidth(text);
          const textHeight = fontSize * 0.4;
          pdf.setFillColor(255, 235, 59); // Amarelo
          pdf.rect(x - 1, y - textHeight + 1, textWidth + 2, textHeight, 'F');
          
          // Adicionar texto
          pdf.setTextColor(0, 0, 0); // Preto
          pdf.text(text, x, y);
          
          return y + textHeight;
        };
        
        const addHeader = () => {
          if (headerImg) {
            pdf.addImage(headerImg, 'JPEG', 0, 0, 210, 0, undefined, 'FAST');
          }
        };
        
        const addFooter = () => {
          if (footerImg) {
            pdf.addImage(footerImg, 'JPEG', 0, 280, 210, 0, undefined, 'FAST');
          }
        };
        
        // PÁGINA 1
        addHeader();
        let yPos = 50; // Aumentar para começar abaixo do cabeçalho
        
        yPos = addText('CONTRATO DE PRESTAÇÃO DE SERVIÇO DE RECREAÇÃO INFANTIL', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold', align: 'center' 
        }) + 5;
        
        yPos = addText('IDENTIFICAÇÃO DAS PARTES CONTRATANTES', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        const contratanteText = `CONTRATANTE: ${contract.contratante?.toUpperCase()}, ${contract.nacionalidade?.toUpperCase() || "BRASILEIRO"}, ${contract.estadoCivil?.toUpperCase()}, ${contract.profissao?.toUpperCase() || "DO LAR"}${contract.identidade ? `, RG nº ${contract.identidade}` : ''}, ${(() => {
          // Verificar se tem CNPJ primeiro (prioridade)
          if (contract.cnpj && contract.cnpj.trim() !== '') {
            return `CNPJ nº ${contract.cnpj}`;
          }
          // Se não tem CNPJ, verificar CPF
          if (contract.cpf && contract.cpf.trim() !== '') {
            return `C.P.F. nº ${contract.cpf}`;
          }
          // Se não tem nenhum, mostrar N/A
          return 'C.P.F. nº N/A';
        })()}, Tel. ${contract.telefone}, residente e domiciliado na ${contract.endereco?.toUpperCase()}, nº ${contract.numero}${contract.complemento ? `, ${contract.complemento.toUpperCase()}` : ''}, Bairro ${contract.bairro?.toUpperCase()}, Cep ${contract.cep}, Cidade ${contract.cidade?.toUpperCase()}, no Estado ${contract.estado?.toUpperCase()}.`;
        // Destacar toda a seção do contratante
        pdf.setFillColor(255, 235, 59); // Amarelo
        pdf.rect(14, yPos - 4, 182, 6, 'F');
        yPos = addText(contratanteText, 15, yPos, { align: 'justify', fontStyle: 'bold' }) + 1;
        
        const contratadaText = `CONTRATADA: Play Kids Recreação Infantil, com sede em Manhuaçu, na Rua Faustino Amâncio, nº 91, bairro Santo Antônio, Cep 36.900-000, no Estado de MG, C.N.P.J. 31.692.815/0001-02, neste ato representada pelo seu diretor RODRIGO PEREIRA DE OLIVEIRA, brasileiro, Casado, Professor, Carteira de Identidade nº MG 12 682 959, C.P.F. nº 016.331.906-50, residente e domiciliado na Rua Faustino Amâncio, nº 91 bairro Santo Antônio, Cep 36.900-157, Cidade Manhuaçu, no Estado MG.`;
        yPos = addText(contratadaText, 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('As partes acima identificadas têm, entre si, justo e acertado o presente Contrato de Prestação de Serviço de Recreação Infantil, que se regerá pelas cláusulas seguintes e pelas condições de pagamento descritas no presente.', 15, yPos, { align: 'justify' }) + 3;
        
        yPos = addText('DO OBJETO DO CONTRATO', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        yPos = addText('CLÁUSULA 1ª - O presente instrumento tem como objeto a prestação de serviços de recreação infantil.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText(`CLÁUSULA 2ª - O Pacote escolhido foi o: ${(contract.pacoteAtividades || "PERSONALIZADO").toUpperCase()}, contendo as seguintes atividades:`, 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText((contract.temaFesta || "TEMA PERSONALIZADO").toUpperCase(), 15, yPos, { 
          fontStyle: 'bold' 
        }) + 1;

        // Adicionar observações se existirem
        if (contract.observacoes && contract.observacoes.trim() !== '') {
          // Limpeza mais abrangente do texto
          let cleanObservations = contract.observacoes
            .replace(/\*([^*]+)\*/g, '$1') // Remove asteriscos de markdown
            .replace(/[`Ø<B^]/g, '') // Remove caracteres especiais
            .replace(/🎈|🤡/g, '') // Remove emojis
            .replace(/[^\w\s\-.,:;!?()áàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ]/g, '') // Remove caracteres especiais mas preserva acentos e ç
            .replace(/\s+/g, ' ') // Remove espaços múltiplos
            .replace(/\s*-\s*/g, ' - ') // Padroniza hífens
            .trim();
          
          const observationsText = `Observações: ${cleanObservations}`;
          
          // Calcular altura necessária para o highlight
          const lines = pdf.splitTextToSize(observationsText, 180);
          const lineHeight = 10 * 0.4; // fontSize * 0.4
          const highlightHeight = lines.length * lineHeight + 4;
          
          pdf.setFillColor(255, 235, 59); // Amarelo
          pdf.rect(14, yPos - 4, 182, highlightHeight, 'F');
          
          yPos = addText(observationsText, 15, yPos, { 
            fontStyle: 'normal' 
          }) + 2;
        } else {
          yPos += 2;
        }
        
        yPos = addText('DA FESTA', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        // CLÁUSULA 3ª com destaque
        const clause3Text = `CLÁUSULA 3ª - A festa será realizada no dia ${contract.dataFesta ? formatDateLong(contract.dataFesta) : 'Data não informada'} às ${contract.horarioInicioFesta} horas, no(a) endereço ${(contract.enderecoFesta || contract.endereco)?.toUpperCase()}.`;
        pdf.setFillColor(255, 235, 59); // Amarelo
        pdf.rect(14, yPos - 4, 182, 6, 'F');
        yPos = addText(clause3Text, 15, yPos, { align: 'justify', fontStyle: 'bold' }) + 1;
        
        // CLÁUSULA 4ª com destaque
        const startTime = contract.inicioRecreacao || contract.horarioInicioFesta;
        const endTime = contract.fimRecreacao || (() => {
          if (!startTime) return 'N/A';
          const [hours, minutes] = startTime.split(':').map(Number);
          const startMinutes = hours * 60 + minutes;
          const endMinutes = startMinutes + 90; // 1h30min = 90 minutos
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
        })();
        
        const clause4Text = `CLÁUSULA 4ª - A recreação terá início às ${startTime} horas, encerrando-se às ${endTime} horas.`;
        pdf.setFillColor(255, 235, 59); // Amarelo
        pdf.rect(14, yPos - 4, 182, 6, 'F');
        yPos = addText(clause4Text, 15, yPos, { align: 'justify', fontStyle: 'bold' }) + 1;
        
        yPos = addText('Parágrafo único. O tempo previsto para início e término do serviço deverá ser respeitado e em caso de atraso no início por parte do CONTRATANTE, a CONTRATADA se reserva o direito de encerrar as atividades no horário previsto.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 5ª - Se o CONTRATANTE desejar ampliar o horário da recreação, deverá ser em comum acordo com a CONTRATADA, e caso haja acordo, será cobrada taxa extra no valor de R$ 450,00. (não ficamos fração de hora).', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText(`CLÁUSULA 6ª - A quantidade de crianças será de aproximadamente ${contract.numeroConvidados}, devendo este número ser comunicado à CONTRATADA com 5 dias de antecedência.`, 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText(`CLÁUSULA 7ª - O aniversariante chama-se ${contract.nomeAniversariante?.toUpperCase()}, o tema da festa é ${contract.temaFesta?.toUpperCase()}, os pais do aniversariante ${getMusicalRestrictionText(contract.restricaoMusical)} restrição a músicas.`, 15, yPos, { align: 'justify' }) + 1;
        
        addFooter();
        
        // PÁGINA 2
        pdf.addPage();
        addHeader();
        yPos = 50; // Aumentar para começar abaixo do cabeçalho
        
        yPos = addText('DAS OBRIGAÇÕES DO CONTRATANTE', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        yPos = addText('CLÁUSULA 8ª - O CONTRATANTE compromete-se a manter a disposição da CONTRATADA todos os meios necessários para execução dos serviços, ou seja, energia elétrica, iluminação, 04 cadeiras, local adequado para a realização das atividades e lanche para os Recreadores.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 9ª - Se o evento for realizado em condomínio ou prédio, o CONTRATANTE deverá avisar com antecedência aos seguranças e à portaria a chegada dos funcionários da CONTRATADA, facilitando a entrada com veículo nas dependências, para carga e descarga de material.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 10ª - O CONTRATANTE deverá efetuar o pagamento na forma e condições estabelecidas na cláusula 14ª.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 11ª - O CONTRATANTE deverá avisar com antecedência de no mínimo 5 dias em caso de alteração da data e horário da festa para que seja acordada nova data e horário, disponível com a agenda da CONTRATADA, desde que haja disponibilidade na mesma.', 15, yPos, { align: 'justify' }) + 3;
        
        yPos = addText('DAS OBRIGAÇÕES DA CONTRATADA', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        yPos = addText('CLÁUSULA 12ª - É dever da CONTRATADA oferecer ao contratante a cópia do presente instrumento, contendo todas as especificidades da prestação de serviço contratado.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 13ª - Se houver atraso por parte da CONTRATADA, esta deverá compensar ao final da recreação.', 15, yPos, { align: 'justify' }) + 3;
        
        yPos = addText('DO PREÇO E DAS CONDIÇÕES DE PAGAMENTO', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        yPos = addText(`CLÁUSULA 14ª - O presente serviço será remunerado pela quantia de R$ ${contract.valor} (${formatValueLong(contract.valor)}), devendo ser pago no ato da assinatura do contrato. O contrato só terá valor mediante envio do comprovante de pagamento do valor estipulado acima. Conta para transferência:`, 15, yPos, { align: 'justify' }) + 2;
        
        yPos = addText('BANCO CC CREDILIVRE LTDA.', 15, yPos, { 
          fontStyle: 'bold', align: 'right'
        }) + 1;
        
        yPos = addText('SICOOB CREDILIVRE, AG 3049, CONTA CORRENTE 57 559 3, BANCO: 756, CNPJ: 31 692', 15, yPos) + 1;
        yPos = addText('815 0001-02 ou pode ser enviado através do PIX 31 692 815 0001 02', 15, yPos) + 1;
        yPos = addText('(conferir cuidadosamente as informações antes de transferir).', 15, yPos, { align: 'right' }) + 2;
        
        yPos = addText('Parágrafo único. Pagamentos onde houver parcelas, o mesmo deverá ser feito no dia estipulado, o não pagamento em dia acarretará multa diária de R$ 15,00.', 15, yPos, { align: 'justify' }) + 1;
        
        addFooter();
        
        // PÁGINA 3
        pdf.addPage();
        addHeader();
        yPos = 50; // Aumentar para começar abaixo do cabeçalho
        
        yPos = addText('DA RESCISÃO IMOTIVADA ou POR CASOS FORTUITOS e FORÇAS MAIORES', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        yPos = addText('CLÁUSULA 15ª - O presente contrato poderá ser rescindido por qualquer das partes, mediante aviso prévio de 5 dias, sem ônus para qualquer das partes.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 16ª - Em caso de rescisão por parte do CONTRATANTE, com menos de 5 dias de antecedência, será cobrado 50% do valor total do contrato.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 17ª - Em caso de rescisão por parte do CONTRATANTE, no dia do evento, será cobrado 100% do valor total do contrato.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 18ª - Em caso de rescisão por parte da CONTRATADA, com menos de 5 dias de antecedência, será devolvido 100% do valor pago pelo CONTRATANTE.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 19ª - Em caso de rescisão por parte da CONTRATADA, no dia do evento, será devolvido 100% do valor pago pelo CONTRATANTE, mais 50% do valor total do contrato a título de multa.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 20ª - Em caso de força maior ou caso fortuito, que impossibilite a realização do evento, o contrato será rescindido sem ônus para qualquer das partes.', 15, yPos, { align: 'justify' }) + 3;
        
        yPos = addText('DAS DISPOSIÇÕES FINAIS', 15, yPos, { 
          fontSize: 10, fontStyle: 'bold' 
        }) + 2;
        
        yPos = addText('CLÁUSULA 21ª - O presente contrato entra em vigor na data de sua assinatura e permanece válido até o cumprimento de todas as obrigações nele previstas.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 22ª - Os casos omissos serão resolvidos de comum acordo entre as partes.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 23ª - Este contrato é regido pelas leis brasileiras e qualquer controvérsia será resolvida no foro da comarca de Manhuaçu/MG.', 15, yPos, { align: 'justify' }) + 1;
        
        yPos = addText('CLÁUSULA 24ª - O presente instrumento é firmado em 2 (duas) vias de igual teor e forma, para um só efeito.', 15, yPos, { align: 'justify' }) + 5;
        
        yPos = addText('CONTRATANTE', 15, yPos, { 
          fontStyle: 'bold', align: 'center'
        }) + 1;
        yPos = addText(contract.contratante?.toUpperCase() || '', 15, yPos, { align: 'center' }) + 1;
        yPos = addText(`${(() => {
          // Verificar se tem CNPJ primeiro (prioridade)
          if (contract.cnpj && contract.cnpj.trim() !== '') {
            return `CNPJ: ${contract.cnpj}`;
          }
          // Se não tem CNPJ, verificar CPF
          if (contract.cpf && contract.cpf.trim() !== '') {
            return `CPF: ${contract.cpf}`;
          }
          // Se não tem nenhum, mostrar N/A
          return 'CPF: N/A';
        })()}`, 15, yPos, { align: 'center' }) + 3;
        
        yPos = addText('CONTRATADA', 15, yPos, { 
          fontStyle: 'bold', align: 'center'
        }) + 1;
        yPos = addText('RODRIGO PEREIRA DE OLIVEIRA', 15, yPos, { align: 'center' }) + 1;
        yPos = addText('CNPJ: 31.692.815/0001-02', 15, yPos, { align: 'center' }) + 3;
        
        yPos = addText(`Manhuaçu/MG, ${contract.dataFesta && formatDateLong(contract.dataFesta)}`, 15, yPos, { align: 'center' });
        
        addFooter();
        
        const pdfBlob = pdf.output('blob');
        resolve(pdfBlob);

      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        reject(error);
      }
    });
  }
};
